import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import type { LostReason } from '@/features/crm/deals/constants';
import * as api from './api';
import type { AddLineInput, ProposalInput } from './types';

export const proposalKeys = {
  all: ['proposals'] as const,
  lists: () => [...proposalKeys.all, 'list'] as const,
  list: (params: api.ProposalListParams) => [...proposalKeys.lists(), params] as const,
  detail: (id: string | number) => [...proposalKeys.all, 'detail', id] as const,
};

export function useProposals(params: api.ProposalListParams, enabled = true) {
  return useQuery({
    queryKey: proposalKeys.list(params),
    queryFn: () => api.listProposals(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useProposal(id: string | number) {
  return useQuery({
    queryKey: proposalKeys.detail(id),
    queryFn: () => api.getProposal(id),
    enabled: Boolean(id),
  });
}

export function useCreateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProposalInput) => api.createProposal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      toast.success('Proposal created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateProposal(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ProposalInput>) => api.updateProposal(id, input),
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      qc.setQueryData(proposalKeys.detail(id), proposal);
      toast.success('Proposal updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveProposal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      toast.success('Proposal archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreProposal(id),
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      qc.setQueryData(proposalKeys.detail(proposal.public_id), proposal);
      toast.success('Proposal restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeProposal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      toast.success('Proposal permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useAddProposalLine(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddLineInput) => api.addProposalLine(id, input),
    onSuccess: (proposal) => {
      // The endpoint returns the recalculated proposal (new line + subtotal/base_total).
      // Write every key the detail page might read by — it's looked up by public_id
      // (the URL param) but callers can pass the numeric pk too (ADR-041).
      qc.setQueryData(proposalKeys.detail(id), proposal);
      qc.setQueryData(proposalKeys.detail(proposal.public_id), proposal);
      qc.setQueryData(proposalKeys.detail(proposal.id), proposal);
      qc.invalidateQueries({ queryKey: proposalKeys.lists() });
      toast.success('Line added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

/**
 * Accept the proposal → backend flips status, advances the deal to WON, and
 * creates a Project (returned). Invalidates proposals, deals, and projects.
 */
export function useAcceptProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, project_name }: { id: string | number; project_name?: string }) =>
      api.acceptProposal(id, { project_name }),
    onSuccess: () => {
      // Invalidate the whole proposal key space, not just lists: the detail
      // query is keyed by whatever id the route used (public_id), so targeting
      // one id variant leaves the open detail page stale (status/actions).
      qc.invalidateQueries({ queryKey: proposalKeys.all });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Proposal accepted — project created');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Accept **without** provisioning a project (optionally linking an existing one
 * onto the drafted contract). Invalidates proposals, deals and contracts.
 */
export function useAcceptProposalOnly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, project }: { id: string | number; project?: number }) =>
      api.acceptProposalOnly(id, { project }),
    onSuccess: (proposal) => {
      // Seed both id variants for an instant update, then invalidate the whole
      // key space so nothing keyed differently can linger (the detail route
      // uses public_id).
      qc.setQueryData(proposalKeys.detail(proposal.public_id), proposal);
      qc.setQueryData(proposalKeys.detail(proposal.id), proposal);
      qc.invalidateQueries({ queryKey: proposalKeys.all });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Proposal accepted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      lost_reason,
    }: {
      id: string | number;
      reason?: string;
      lost_reason?: LostReason;
    }) => api.rejectProposal(id, { reason, lost_reason }),
    onSuccess: (proposal) => {
      qc.setQueryData(proposalKeys.detail(proposal.public_id), proposal);
      qc.setQueryData(proposalKeys.detail(proposal.id), proposal);
      qc.invalidateQueries({ queryKey: proposalKeys.all });
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.success('Proposal marked lost');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Draft a Contract from this proposal (spine step Proposal → Contract). Returns
 * the new DRAFT contract. Invalidates the contracts list.
 */
export function useCreateContractFromProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.createContractFromProposal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract drafted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
